<script>
  import { t, locale } from '../i18n';
  import { styleName } from '../content/publicSite.js';
  import { editStore, settingsStore, imageStore, sessionStore } from '../stores';
  import { applySelectedFeature, SUBJECT_NAME_STYLE_NAMES, limitSubjectName } from '../utils/apiUtils.js';
  import { detectObjects } from '../utils/apiClient.js';

  import { styleCategories, orderedStyles, advancedStyleOptions, emoticonStyleOptions, fruitOptions, groomingBreeds } from '../utils/styleCatalog';
  import { costStore } from '../stores/costStore';
  import { serviceStore } from '../stores/serviceStore';
  import MbtiQuiz from './MbtiQuiz.svelte';
  let applying = false;
  $: costKey = ['emoticon_style', 'realistic_emoticon_style'].includes(feature?.id) ? 'advanced_style_transform' : feature?.id;
  $: featureCost = $costStore[costKey] ?? 0;
  $: allStyleOptions = orderedStyles($settingsStore.recentStyles || []);

  let selectedStyleCategory = '전체';

  $: feature = $editStore.selectedFeature;
  $: isUploadFeature = feature && feature.id === 'upload_image';
  $: isStyleTransform = feature && ['style_transform', 'advanced_style_transform', 'emoticon_style'].includes(feature.id);
  $: styleOptionsForFeature = feature?.id === 'advanced_style_transform'
    ? advancedStyleOptions
    : feature?.id === 'emoticon_style'
      ? emoticonStyleOptions
      : allStyleOptions;
  $: if (isStyleTransform && !styleOptionsForFeature.some(s => s.name === $settingsStore.selectedStyle)) {
    selectStyle(styleOptionsForFeature[0]);
  }
  $: styleCategoriesForFeature = feature?.id === 'style_transform'
    ? styleCategories
    : ['전체'];
  $: selectedOption = styleOptionsForFeature.find(style => style.name === $settingsStore.selectedStyle);
  $: if (isStyleTransform && selectedOption &&
    (($settingsStore.selectedStyleBackend || '') !== (selectedOption.backend || '') ||
    ($settingsStore.selectedStyleBackendName || '') !== (selectedOption.backendName || ''))) {
    selectStyle(selectedOption);
  }
  $: if (!styleCategoriesForFeature.includes(selectedStyleCategory)) {
    selectedStyleCategory = '전체';
  }
  $: filteredStyleOptions = selectedStyleCategory === '전체'
    ? styleOptionsForFeature
    : styleOptionsForFeature.filter(s => s.category === selectedStyleCategory);
  $: requiresSubjectName = isStyleTransform && SUBJECT_NAME_STYLE_NAMES.has($settingsStore.selectedStyle);

  function selectStyle(style) {
    $settingsStore.selectedStyle = style.name;
    $settingsStore.selectedStyleBackend = style.backend;
    $settingsStore.selectedStyleBackendName = style.backendName;

    if (!SUBJECT_NAME_STYLE_NAMES.has(style.name)) {
      $settingsStore.subjectName = '';
    }
  }

  function updateSubjectName(event) {
    $settingsStore.subjectName = limitSubjectName(event.currentTarget.value);
  }
  
  // 프리셋 옵션 선택 시 확장 픽셀 계산
  function selectPreset(preset) {
    $settingsStore.outpaintSettings.selectedPreset = preset;
    
    // 원본 이미지 크기 가져오기
    const originalWidth = $imageStore.imageWidth || 1024;
    const originalHeight = $imageStore.imageHeight || 1024;
    
    // 프리셋에 따라 확장 픽셀 계산
    switch(preset) {
      case 'FHD':
        // 1920x1080 (Full HD)
        calculateExpansion(1920, 1080);
        break;
      case 'QHD':
        // 2560x1440 (Quad HD)
        calculateExpansion(2560, 1440);
        break;
      case '4K':
        // 3840x2160 (4K UHD)
        calculateExpansion(3840, 2160);
        break;
      case '16:9':
        // 원본 높이를 유지하면서 16:9 비율로 확장
        const targetWidth16_9 = Math.round(originalHeight * (16/9));
        calculateExpansion(targetWidth16_9, originalHeight);
        break;
      case '4:3':
        // 원본 높이를 유지하면서 4:3 비율로 확장
        const targetWidth4_3 = Math.round(originalHeight * (4/3));
        calculateExpansion(targetWidth4_3, originalHeight);
        break;
      case '3:4':
        // 원본 너비를 유지하면서 3:4 비율로 확장
        const targetHeight3_4 = Math.round(originalWidth * (4/3));
        calculateExpansion(originalWidth, targetHeight3_4);
        break;
      case '9:16':
        // 원본 너비를 유지하면서 9:16 비율로 확장
        const targetHeight9_16 = Math.round(originalWidth * (16/9));
        calculateExpansion(originalWidth, targetHeight9_16);
        break;
      case '1:1':
        // 정사각형으로 확장
        const maxDimension = Math.max(originalWidth, originalHeight);
        calculateExpansion(maxDimension, maxDimension);
        break;
    }
  }
  
  // 목표 크기에 맞게 확장 픽셀 계산
  function calculateExpansion(targetWidth, targetHeight) {
    const originalWidth = $imageStore.imageWidth || 1024;
    const originalHeight = $imageStore.imageHeight || 1024;
    
    // 좌우 확장 픽셀 계산
    const totalWidthExpansion = targetWidth - originalWidth;
    const leftExpansion = Math.floor(totalWidthExpansion / 2);
    const rightExpansion = totalWidthExpansion - leftExpansion;
    
    // 상하 확장 픽셀 계산
    const totalHeightExpansion = targetHeight - originalHeight;
    const upExpansion = Math.floor(totalHeightExpansion / 2);
    const downExpansion = totalHeightExpansion - upExpansion;
    
    // 음수 값은 0으로 설정 (축소 방지)
    $settingsStore.outpaintSettings.left = Math.max(0, leftExpansion);
    $settingsStore.outpaintSettings.right = Math.max(0, rightExpansion);
    $settingsStore.outpaintSettings.up = Math.max(0, upExpansion);
    $settingsStore.outpaintSettings.down = Math.max(0, downExpansion);
    
    // 이미지 위치 초기화
    $settingsStore.outpaintSettings.imagePosition = { x: 0, y: 0 };
  }
  
  // 적용하기 버튼 클릭 핸들러
  async function handleApply() {
    if (applying) return;
    applying = true;
    try { await applySelectedFeature(); } finally { applying = false; }
  }

  // 객체 감지 핸들러
  async function handleDetectObjects() {
    if (!$imageStore.uploadedImage) {
      alert('원본 이미지가 없습니다.');
      return;
    }
    
    $settingsStore.isDetecting = true;
    try {
      const data = await detectObjects($imageStore.uploadedImage);
      if (data && data.objects) {
        $settingsStore.detectedObjects = data.objects;
      } else if (data && data.raw) {
        $settingsStore.detectedObjects = data.raw.split(/[\s,]+/).filter(Boolean);
      } else {
        $settingsStore.detectedObjects = ['object'];
      }
    } catch (error) {
      console.error('객체 감지 실패:', error);
      $settingsStore.detectedObjects = ['object'];
    } finally {
      $settingsStore.isDetecting = false;
    }
  }

  // 객체 선택 토글
  function toggleObjectSelection(obj) {
    if ($settingsStore.selectedObjects.includes(obj)) {
      $settingsStore.selectedObjects = $settingsStore.selectedObjects.filter(item => item !== obj);
    } else {
      $settingsStore.selectedObjects = [...$settingsStore.selectedObjects, obj];
    }
    
    // 선택된 객체들을 프롬프트에 자동으로 채워줌 (쉼표로 구분)
    $settingsStore.removeObjectsPrompt = $settingsStore.selectedObjects.join(', ');
  }
</script>

<div class="option-panel {isUploadFeature ? 'no-apply' : ''}">
  {#if feature}
    <div class="options-content {isUploadFeature ? 'no-scroll' : ''} {isStyleTransform ? 'style-mode' : ''}">
    <div class="feature-header">
      <!-- <span class="feature-icon" aria-hidden="true">{feature.icon}</span> -->
      <h2 class="feature-title">{$t(`feature.${feature.id}`)}</h2>
    </div>
    <p class="feature-description">{feature.description}</p>

    {#if feature.id === 'upload_image'}
      <p class="info-text">과거 작업은 대시보드에서 볼 수 있습니다.</p>
      <p class="info-text">이미지는 우측 이미지 영역에서 업로드합니다.</p>
      <div class="upload-tips">
        <h4>💡 팁</h4>
        <div class="upload-tip-grid">
          <div class="tip-item">
            <img src="/tips/tip1.jpg" alt="tip1" />
            <p>반려견이 잘 보이는 사진을<br>선택하세요.</p>
          </div>
          <div class="tip-item">
            <img src="/tips/tip2.jpg" alt="tip2" />
            <p>반려견 전신이 다 보이면 더<br>좋은 결과를 얻을 수 있습니다.</p>
          </div>
          <div class="tip-item">
            <img src="/tips/tip3.png" alt="tip3" />
            <p>이미지 해상도가 너무 높으면<br>편집이 거부될 수 있습니다.</p>
          </div>
          <div class="tip-item">
            <img src="/tips/tip4.jpg" alt="tip4" />
            <p>반려견이 가려진 부분이 많거나 배경 색깔과 비슷하면 결과가 왜곡될 수 있습니다.</p>
          </div>
        </div>
      </div>
    {:else if feature.id === 'replace_background'}
      <div class="option-group">
        <label for="background-select" class="option-label">배경 선택:</label>
        <select 
          id="background-select" 
          bind:value={$settingsStore.selectedBackground} 
          class="styled-select"
          aria-describedby="background-help"
        >
          <option value="눈밭">눈밭</option>
          <option value="해변">해변</option>
          <option value="사막">사막</option>
          <option value="미래도시(낮)">미래도시(낮)</option>
          <option value="미래도시(밤)">미래도시(밤)</option>
          <option value="벚꽃이 만개한 공원">벚꽃이 만개한 공원</option>
          <option value="깊은 협곡">깊은 협곡</option>
          <option value="그랜드캐니언">그랜드캐니언</option>
          <option value="그리스 신전">그리스 신전</option>
          <option value="우주정거장 내부">우주정거장 내부</option>
          <option value="환상의 디즈니랜드">환상의 디즈니랜드</option>
        </select>
        <p id="background-help" class="help-text">반려견 이미지의 배경을 선택한 장면으로 교체합니다.</p>
      </div>
    {:else if feature.id === 'search_replace'}
      <div class="option-group">
        <label for="replace-prompt" class="option-label">변경할 객체 설명:</label>
        <input 
          type="text" 
          id="replace-prompt" 
          bind:value={$settingsStore.searchReplacePrompt} 
          placeholder="예: a victorian queen" 
          class="styled-input"
          aria-describedby="replace-help" 
        />
        <p id="replace-help" class="help-text">반려견을 어떤 모습으로 변경할지 영어로 자세히 설명해주세요.</p>
      </div>
    {:else if feature.id === 'outpaint'}
      <div class="option-group">
        <!-- 프리셋 옵션 추가 -->
        <fieldset class="preset-options">
          <legend class="option-label">확장 프리셋:</legend>
          <div class="preset-buttons" role="radiogroup">
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === 'FHD'}
              on:click={() => selectPreset('FHD')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === 'FHD'}
            >
              FHD
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === 'QHD'}
              on:click={() => selectPreset('QHD')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === 'QHD'}
            >
              QHD
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === '4K'}
              on:click={() => selectPreset('4K')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === '4K'}
            >
              4K
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === '16:9'}
              on:click={() => selectPreset('16:9')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === '16:9'}
            >
              16:9
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === '4:3'}
              on:click={() => selectPreset('4:3')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === '4:3'}
            >
              4:3
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === '3:4'}
              on:click={() => selectPreset('3:4')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === '3:4'}
            >
              3:4
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === '9:16'}
              on:click={() => selectPreset('9:16')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === '9:16'}
            >
              9:16
            </button>
            <button 
              type="button"
              class="preset-btn" 
              class:active={$settingsStore.outpaintSettings.selectedPreset === '1:1'}
              on:click={() => selectPreset('1:1')}
              aria-pressed={$settingsStore.outpaintSettings.selectedPreset === '1:1'}
            >
              1:1
            </button>
          </div>
        </fieldset>
        <div class="outpaint-info">
          <p class="info-text">
            초록색 영역이 확장될 부분입니다.<br>이미지를 드래그하여 위치를 조정할 수 있습니다.
          </p>
          <div class="resolution-info">
            <span>원본 크기: {$imageStore.imageWidth || 0} x {$imageStore.imageHeight || 0}</span>
            <span>최종 크기: {($imageStore.imageWidth || 0) + $settingsStore.outpaintSettings.left + $settingsStore.outpaintSettings.right} x {($imageStore.imageHeight || 0) + $settingsStore.outpaintSettings.up + $settingsStore.outpaintSettings.down}</span>
          </div>
        </div>
        <label for="outpaint-creativity" class="option-label">
          창의성 수준: {$settingsStore.outpaintSettings.creativity.toFixed(1)}
        </label>
        <div class="slider-container">
          <input 
            type="range" 
            id="outpaint-creativity" 
            bind:value={$settingsStore.outpaintSettings.creativity} 
            min="0" 
            max="1" 
            step="0.1" 
            class="styled-slider"
            aria-valuemin="0"
            aria-valuemax="1"
            aria-valuenow={$settingsStore.outpaintSettings.creativity}
          />
          <div class="slider-labels">
            <span>사실적</span>
            <span>창의적</span>
          </div>
        </div>
        <br>
        <label for="outpaint-prompt" class="option-label">프롬프트 (선택사항):</label>
        <textarea 
          id="outpaint-prompt" 
          bind:value={$settingsStore.outpaintSettings.prompt}
          placeholder="확장 영역에 대한 설명을 입력하세요" 
          class="styled-textarea"
          aria-describedby="outpaint-prompt-help"
        ></textarea>
        <p id="outpaint-prompt-help" class="help-text">확장 영역에 생성될 내용을 자세히 설명하면 더 좋은 결과를 얻을 수 있습니다.</p>
      </div>
    {:else if feature.id === 'style_transform' || feature.id === 'advanced_style_transform' || feature.id === 'emoticon_style'}
      <div class="option-group">
        <!-- 카테고리 필터 -->
        <div class="style-category-bar" role="radiogroup" aria-label="스타일 카테고리">
          {#each styleCategoriesForFeature as cat}
            <button
              type="button"
              class="category-chip"
              class:active={selectedStyleCategory === cat}
              on:click={() => selectedStyleCategory = cat}
              aria-pressed={selectedStyleCategory === cat}
            >{cat}</button>
          {/each}
        </div>
        <!-- 스타일 그리드 -->
        <div class="style-options">
          {#each filteredStyleOptions as style}
            <button
              type="button"
              class="style-option"
              class:selected={$settingsStore.selectedStyle === style.name}
              on:click={() => selectStyle(style)}
              aria-pressed={$settingsStore.selectedStyle === style.name}
            >
              <img src={style.image} alt={styleName($locale, style.name)} />
              {#if style.isNew}<span class="new-badge">NEW</span>{/if}
              <span>{styleName($locale, style.name)}</span>
            </button>
          {/each}
        </div>
        {#if $settingsStore.selectedStyle === '과일 인형탈'}
          <label for="fruit-type" class="option-label">과일 선택</label>
          <select id="fruit-type" bind:value={$settingsStore.fruitType} class="styled-select">
            {#each fruitOptions as fruit}<option value={fruit}>{fruit}</option>{/each}
          </select>
        {/if}
        {#if $settingsStore.selectedStyle === '미용 스타일'}
          <label for="grooming-breed" class="option-label">반려견 품종</label>
          <select id="grooming-breed" bind:value={$settingsStore.groomingBreed} class="styled-select">
            <option value="">품종을 선택해 주세요</option>
            {#each groomingBreeds as breed}<option value={breed}>{breed}</option>{/each}
          </select>
          <p class="help-text">품종에 어울리는 네 가지 미용 스타일을 보여줍니다.</p>
        {/if}
        {#if requiresSubjectName}
          <div class="subject-name-option">
            <label for="subject-name" class="option-label">주인공 이름</label>
            <input
              type="text"
              id="subject-name"
              value={$settingsStore.subjectName}
              on:input={updateSubjectName}
              placeholder="예: 복실이"
              maxlength="20"
              class="styled-input"
              autocomplete="off"
              aria-describedby="subject-name-help"
            />
            <p id="subject-name-help" class="help-text subject-name-help">
              이미지에 표시할 이름입니다. 비워 두면 puppy가 사용되며, 한글은 최대 10자, 그 외 언어는 최대 20자까지 입력할 수 있어요.
            </p>
          </div>
        {/if}
      </div>
    {:else if feature.id === 'find_mbti'}
      <div class="option-group"><MbtiQuiz /></div>
    {:else if feature.id === 'make_video'}
      <div class="option-group">
        <label for="video-style" class="option-label">영상 스타일</label>
        <select id="video-style" bind:value={$settingsStore.videoStyle} class="styled-select">
          <option value="kpop_dance">K-POP 댄스</option>
          <option value="walk">걷는 모습</option>
          <option value="playful">재롱부리기</option>
        </select>
        <p class="help-text">생성에 약 5~10분이 걸릴 수 있습니다. 반려견의 전신이 잘 보이는 사진을 권장합니다.</p>
      </div>
    {:else if feature.id === 'inpaint'}
      <div class="option-group">
        <p class="canvas-instruction">
          <!-- <span class="instruction-icon" aria-hidden="true">🖌️</span> -->
          캔버스에 인페인팅할 영역을 그려주세요.
        </p>
        <label for="inpaint-prompt" class="option-label">프롬프트:</label>
        <textarea 
          id="inpaint-prompt" 
          bind:value={$settingsStore.inpaintSettings.prompt}
          placeholder="인페인팅 영역에 생성할 내용을 설명하세요" 
          class="styled-textarea"
          aria-describedby="inpaint-prompt-help"
          required
        ></textarea>
        <p id="inpaint-prompt-help" class="help-text">그린 영역에 생성될 내용을 자세히 설명해주세요.</p>
        
        <label for="inpaint-negative-prompt" class="option-label">네거티브 프롬프트 (선택사항):</label>
        <textarea 
          id="inpaint-negative-prompt" 
          bind:value={$settingsStore.inpaintSettings.negativePrompt}
          placeholder="생성하지 않을 요소를 설명하세요" 
          class="styled-textarea"
          aria-describedby="inpaint-negative-help"
        ></textarea>
        <p id="inpaint-negative-help" class="help-text">생성하지 않을 요소를 설명하면 원치 않는 결과를 방지할 수 있습니다.</p>
        
        <label for="inpaint-seed" class="option-label">시드 (0 = 랜덤):</label>
        <input 
          type="number" 
          id="inpaint-seed" 
          bind:value={$settingsStore.inpaintSettings.seed} 
          min="0" 
          class="styled-number"
          aria-describedby="inpaint-seed-help"
        />
        <p id="inpaint-seed-help" class="help-text">같은 시드값을 사용하면 비슷한 결과를 얻을 수 있습니다.</p>
      </div>
    {:else if feature.id === 'erase_object'}
      <div class="option-group">
        <p class="canvas-instruction">
          <!-- <span class="instruction-icon" aria-hidden="true">🖌️</span> -->
          캔버스에 지울 객체를 그려주세요.
        </p>
        <div class="instruction-steps">
          <ol>
            <li>지우고 싶은 객체 위에 마우스로 영역을 그립니다.</li>
            <li>그린 영역 내의 객체가 자동으로 감지되어 제거됩니다.</li>
            <li>결과가 마음에 들지 않으면 다른 시드값을 시도해보세요.</li>
          </ol>
        </div>
        <label for="erase-seed" class="option-label">시드 (0 = 랜덤):</label>
        <input 
          type="number" 
          id="erase-seed" 
          bind:value={$settingsStore.eraseObjectSeed} 
          min="0" 
          class="styled-number"
          aria-describedby="erase-seed-help"
        />
        <p id="erase-seed-help" class="help-text">같은 시드값을 사용하면 비슷한 결과를 얻을 수 있습니다.</p>
      </div>
    {:else if feature.id === 'remove_background'}
      <div class="option-group">
        <!-- <div class="feature-card">
          <div class="feature-card-icon" aria-hidden="true">🖼️</div>
          <div class="feature-card-content">
            <p class="feature-card-title">배경 제거</p>
            <p class="feature-card-description">반려견만 남기고 배경을 투명하게 만듭니다.</p>
          </div>
        </div> -->
        <p class="info-text">추가 설정이 필요 없습니다. 적용 버튼을 눌러 배경을 제거하세요.</p>
      </div>
    {:else if feature.id === 'resolution_increase'}
      <div class="option-group">
        <!-- <div class="feature-card">
          <div class="feature-card-icon" aria-hidden="true">🔍</div>
          <div class="feature-card-content">
            <p class="feature-card-title">해상도 증가</p>
            <p class="feature-card-description">이미지의 해상도를 4배 증가시킵니다.</p>
          </div>
        </div> -->
        <p class="info-text">추가 설정이 필요 없습니다.<br>적용 버튼을 눌러 해상도를 증가시키세요.<br></p>
        <p class="info-text">다만 해상도 증가의 경우 이미지 크기 증가로 인해<br>다른 편집을 이어서 진행할 수 없게 될 수 있습니다.</p>
      </div>
    {:else if feature.id === 'object_remove'}
      <div class="option-group">
        <div class="header-with-action">
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="option-label">제거할 대상 선택:</label>
          <button 
            type="button" 
            class="detect-btn" 
            on:click={handleDetectObjects} 
            disabled={$settingsStore.isDetecting}
          >
            {$settingsStore.isDetecting ? '감지 중...' : '객체 감지'}
          </button>
        </div>
        
        {#if $settingsStore.detectedObjects.length > 0}
          <div class="object-chips">
            {#each $settingsStore.detectedObjects as obj}
              <button 
                type="button"
                class="chip" 
                class:selected={$settingsStore.selectedObjects.includes(obj)}
                on:click={() => toggleObjectSelection(obj)}
              >
                {obj}
              </button>
            {/each}
          </div>
        {:else if !$settingsStore.isDetecting}
          <p class="help-text">사진에서 제거하고 싶은 요소를 자동으로 찾으려면 '객체 감지'를 누르세요.</p>
        {/if}

        <label for="remove-objects" class="option-label mt-4">제거할 대상 목록 (직접 입력 가능):</label>
        <input 
          type="text" 
          id="remove-objects" 
          bind:value={$settingsStore.removeObjectsPrompt} 
          placeholder="예: leash, toys, people" 
          class="styled-input"
          aria-describedby="remove-objects-help" 
        />
        <p id="remove-objects-help" class="help-text">쉼표(,)로 구분하여 입력하세요. 예: ball, person</p>
      </div>
    {:else if feature.id === 'image_composite'}
      <div class="option-group">
        <label for="composite-mode" class="option-label">합성 방식:</label>
        <select id="composite-mode" bind:value={$settingsStore.compositeMode} class="styled-select">
          <option value="ai_fitting">AI 피팅</option>
          <option value="running_record">러닝 기록</option>
          <option value="together">함께 어울리기</option>
        </select>
        <p class="help-text">Flutter 앱과 동일한 합성 방식을 선택할 수 있습니다.</p>
        <label for="secondary-image-upload" class="option-label">합성할 이미지 선택:</label>
        <input 
          type="file" 
          id="secondary-image-upload" 
          accept="image/*"
          on:change={(e) => {
            const file = e.target.files[0];
            if (file) {
              $settingsStore.secondaryImageFile = file;
            }
          }}
          class="styled-file-input"
        />
        <p class="help-text">원본 이미지와 자연스럽게 합성할 두 번째 이미지를 업로드해주세요.</p>
      </div>
    {/if}
    </div>

    {#if feature.id !== 'upload_image'}
      <div class="apply-section">
        <button
          type="button"
          class="btn btn-primary apply-btn"
          on:click={handleApply}
          aria-label={$t('apply')}
          disabled={applying || feature.disabled || $serviceStore.blocksNewRequests || $editStore.isLoading}
        >
          {$t('apply')}
          {#if featureCost > 0}
            <span class="coin-badge">
              <img src="/icons/rion_reverse.png" alt="RION" class="coin-icon" />
              {featureCost}
            </span>
          {/if}
        </button>
      </div>
    {/if}
  {/if}
</div>

<style>
  .new-badge { position: absolute; top: 6px; left: 6px; background: #fe6a37; color: white; border-radius: 5px; padding: 2px 5px; font-size: 10px; font-weight: bold; }
  .style-option { position: relative; }
  .option-panel {
    display: grid;
    grid-template-rows: 9fr 1fr;
    padding: 1.5rem;
    border-right: 1px solid #dfe3e8;
    height: 100%;
  }

  .option-panel.no-apply {
    grid-template-rows: 1fr;
  }

  .options-content {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    overflow-y: auto;
  }

  .options-content.no-scroll {
    overflow-y: hidden;
  }

  /* 스타일 변환 전용: 전체 스크롤 막고 스타일 그리드만 스크롤 */
  .options-content.style-mode {
    overflow: hidden;
  }

  .options-content.style-mode .option-group {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .apply-section {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .feature-header {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 0.5rem;
    padding-bottom: 0.8rem;
    border-bottom: 1px solid rgba(255, 99, 130, 0.3);
  }

  /* .feature-icon {
    font-size: 1.8rem;
    color: #FF6382;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
  } */

  .feature-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #FE6A37;
  }

  .feature-description {
    font-size: 0.95rem;
    line-height: 1.5;
    opacity: 0.9;
    margin-top: 0;
    margin-bottom: 1rem;
    padding: 0.8rem;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    border-left: 3px solid #FE6A37;
  }

  :global(.light) .feature-description {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .option-group {
    display: flex;
    flex-direction: column;
    /* gap: 0.5rem; */
  }
  
  .option-label {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
    color: inherit;
  }

  /* .preserve-face-group {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .switch {
    position: relative;
    display: inline-block;
    width: 42px;
    height: 24px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 34px;
  }

  .slider:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }

  .switch input:checked + .slider {
    background-color: #FE6A37;
  }

  .switch input:checked + .slider:before {
    transform: translateX(18px);
  } */
  
  .help-text {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0.2rem 0 0.8rem;
    font-style: italic;
  }
  
  :global(.light) .help-text {
    color: rgba(0, 0, 0, 0.6);
  }
  
  .canvas-instruction {
    font-weight: 600;
    font-size: 1rem;
    color: #FE6A37;
    background-color: rgba(255, 99, 130, 0.1);
    padding: 1rem;
    border-radius: 8px;
    border-left: 4px solid #FE6A37;
    margin: 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  /* .instruction-icon {
    font-size: 1.5rem;
  } */
  
  .instruction-steps {
    background-color: rgba(255, 255, 255, 0.05);
    padding: 1rem;
    border-radius: 8px;
    margin: 0.5rem 0;
  }

  :global(.light) .instruction-steps {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  .instruction-steps ol {
    margin: 0;
    padding-left: 1.5rem;
  }
  
  .instruction-steps li {
    margin-bottom: 0.5rem;
  }
  
  .info-text {
    font-size: 0.95rem;
    background-color: rgba(255, 255, 255, 0.1);
    padding: 0.8rem;
    border-radius: 8px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .upload-tips {
    background-color: rgba(255, 255, 255, 0.05);
    padding: 0.8rem;
    border-radius: 8px;
  }

  :global(.light) .upload-tips {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .upload-tips h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    color: #FE6A37;
  }

  .upload-tip-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }

  .tip-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    font-size: 0.9rem;
  }

  .tip-item img {
    width: 100%;
    border-radius: 6px;
  }
  :global(.light) .info-text {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  .styled-select, .styled-input, .styled-number, .styled-textarea {
    padding: 0.8rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background-color: rgba(0, 0, 0, 0.2);
    color: inherit;
    font-size: 0.95rem;
    width: 100%;
    transition: all 0.3s ease;
    font-family: inherit;
  }
  
  :global(.light) .styled-select, 
  :global(.light) .styled-input, 
  :global(.light) .styled-number, 
  :global(.light) .styled-textarea {
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: #404040;
  }
  
  .styled-select:focus, 
  .styled-input:focus, 
  .styled-number:focus, 
  .styled-textarea:focus {
    outline: none;
    border-color: #FE6A37;
    box-shadow: 0 0 0 2px rgba(255, 99, 130, 0.3);
  }
  
  .styled-textarea {
    min-height: 100px;
    resize: vertical;
  }
  
  .preset-options {
    margin-bottom: 1rem;
  }
  
  .preset-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
  
  .preset-btn {
    padding: 0.6rem 0.8rem;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background-color: rgba(0, 0, 0, 0.2);
    color: inherit;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
  }
  
  :global(.light) .preset-btn {
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: #404040;
  }
  
  .preset-btn:hover {
    background-color: rgba(255, 99, 130, 0.1);
    transform: translateY(-2px);
  }
  
  .preset-btn:focus-visible {
    outline: 2px solid #FE6A37;
    outline-offset: 2px;
  }
  
  .preset-btn.active {
    background-color: rgba(255, 99, 130, 0.2);
    border-color: #FE6A37;
    color: #FE6A37;
    font-weight: 600;
  }

  .style-options {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem;
    margin-top: 0.5rem;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    align-content: start;
  }

  .subject-name-option {
    flex: 0 0 auto;
    margin-top: 0.9rem;
  }

  .subject-name-help {
    margin-bottom: 0;
  }

  .style-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    background: none;
    border: 2px solid transparent;
    padding: 0.5rem;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.3s ease;
    position: relative;
    min-width: 0;
  }

  .style-option.selected {
    border-color: #FE6A37;
  }

  .style-option img {
    width: min(80px, 100%);
    aspect-ratio: 1;
    height: auto;
    object-fit: cover;
    border-radius: 6px;
  }

  .style-option span {
    font-size: 0.85rem;
    text-align: center;
    overflow-wrap: anywhere;
  }

  /* 카테고리 필터 바 */
  .style-category-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 0.75rem;
  }

  .category-chip {
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    border: none;
    background: #F2F2F2;
    color: #555555;
    font-size: 0.8rem;
    font-weight: 400;
    cursor: pointer;
    transition: background 0.18s, color 0.18s;
    white-space: nowrap;
  }

  .category-chip.active {
    background: #ED6D1F;
    color: #ffffff;
    font-weight: 600;
  }

  :global(.dark) .category-chip {
    background: #3a3a3a;
    color: #cccccc;
  }

  :global(.dark) .category-chip.active {
    background: #ED6D1F;
    color: #ffffff;
  }

  /* .backend-badge {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: rgba(0, 0, 0, 0.7);
    color: white !important;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-size: 0.7rem !important;
    font-weight: 600;
  } */
  
  .outpaint-info {
    margin: 1rem 0;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
  }

  :global(.dark) .outpaint-info {
    background-color: rgba(255, 255, 255, 0.05);
  }

  :global(.light) .outpaint-info {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  .resolution-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
    font-size: 0.9rem;
  }
  
  .slider-container {
    width: 100%;
    padding: 0 0.5rem;
  }
  
  .styled-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(to right, #4a90e2, #FF6382);
    outline: none;
  }
  
  .styled-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #FE6A37;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
  }
  
  .styled-slider::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }
  
  .styled-slider::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #FE6A37;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    border: none;
  }
  
  .styled-slider::-moz-range-thumb:hover {
    transform: scale(1.2);
  }
  
  .slider-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 0.5rem;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
  }
  
  :global(.light) .slider-labels {
    color: rgba(0, 0, 0, 0.6);
  }
  
  .btn {
    padding: 1rem;
    border-radius: 8px;
    border: none;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
  }
  
  .btn-primary {
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
  }
  
  .apply-btn {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    padding-left: 2rem;
    padding-right: 2rem;
    width: 100%;
  }
  
  .apply-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }

  /* 코인 배지 */
  .coin-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: rgba(0, 0, 0, 0.18);
    border-radius: 999px;
    padding: 1px 8px 1px 5px;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    margin-left: 6px;
    line-height: 1.4;
  }

  .coin-icon {
    width: 15px;
    height: 15px;
    object-fit: contain;
  }

  .apply-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  .apply-btn:hover::before {
    left: 100%;
  }
  
  .apply-btn:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }
  
  .styled-file-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #dfe3e8;
    border-radius: 4px;
    background: #f9fafb;
    font-size: 0.9rem;
  }
  
  /* 반응형 디자인 */
  @media (max-width: 768px) {
    /* .option-panel {
      padding: 0.5rem;
    } */
    
    .preset-buttons {
      gap: 0.3rem;
    }
    
    .preset-btn {
      padding: 0.5rem 0.6rem;
      font-size: 0.8rem;
    }
  }

  .header-with-action {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .detect-btn {
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    background-color: #FE6A37;
    color: white;
    font-size: 0.85rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
  }

  .detect-btn:hover:not(:disabled) {
    background-color: #e55a2b;
    transform: translateY(-1px);
  }

  .detect-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .object-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
  }

  :global(.light) .object-chips {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .chip {
    padding: 0.3rem 0.7rem;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.1);
    color: inherit;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  :global(.light) .chip {
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .chip.selected {
    background-color: #FE6A37;
    border-color: #FE6A37;
    color: white;
  }

  .mt-4 {
    margin-top: 1rem;
  }
  @media (max-width: 800px) {
    .option-panel { height: auto; min-height: 420px; grid-template-rows: minmax(0, 1fr) auto; padding: 20px 16px; }
    .options-content.style-mode, .options-content.style-mode .option-group { overflow: visible; }
    .style-options { flex: none; max-height: 420px; }
    .apply-section { padding-top: 16px; }
  }
</style>
